import { useState, useEffect, useMemo, useCallback } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import ProductSelector from './ProductSelector';
import TimeRangeSelector from './TimeRangeSelector';
import FilteredPostsDisplay from './FilteredPostsDisplay';
import { apiPost } from '../services/apiClient';
import { DIMENSIONS } from '../constants/dimensions';
import '../styles/DimensionNPSPage.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const CHART_COLORS = [
    '#0057FF', '#FF8A00', '#00A86B', '#7A1FFF', '#E63946',
    '#0891B2', '#CA8A04', '#6366F1', '#EC4899', '#14B8A6',
];

const ROWS_PER_PAGE = 25;

function getNPSClass(nps) {
    if (nps >= 50) return 'nps-positive';
    if (nps <= -10) return 'nps-negative';
    return 'nps-neutral';
}

export default function DimensionNPSPage({ topicMap,
    // Optional shared product selection: adopted when valid for this
    // page's topicMap; selections made here are pushed back up.
    selectedProduct: sharedSelectedProduct, onProductChange }) {
    // Lazy-initialize from the shared selection (if valid) so local state
    // is never empty on first render — see PricingPlot.js for why an
    // empty initial value lets ProductSelector's own internal auto-select
    // effect win a race against the shared-selection logic below.
    const [selectedTopic, setSelectedTopic] = useState(() =>
        (sharedSelectedProduct && topicMap?.[sharedSelectedProduct]) ? sharedSelectedProduct : null
    );

    // Select a default topic if none is selected yet. Prefers the
    // page-wide shared topic selection (if valid here); otherwise falls
    // back to the highest-priority topic. Explicit default logic (rather
    // than relying on ProductSelector's own internal auto-select) avoids a
    // race where ProductSelector — a child component, whose effects run
    // before this parent's — could fire its own default first and push it
    // back up as the shared selection before this effect gets a chance to
    // apply the incoming shared value.
    useEffect(() => {
        if (selectedTopic) return;
        if (sharedSelectedProduct && topicMap?.[sharedSelectedProduct]) {
            setSelectedTopic(sharedSelectedProduct);
            return;
        }
        const sortedTopics = Object.values(topicMap || {}).sort((a, b) => {
            const priorityA = a.priority === undefined ? Infinity : a.priority;
            const priorityB = b.priority === undefined ? Infinity : b.priority;
            return priorityA - priorityB;
        });
        if (sortedTopics[0]) setSelectedTopic(sortedTopics[0].id);
    }, [sharedSelectedProduct, topicMap, selectedTopic]);

    const selectTopic = (topicId) => {
        setSelectedTopic(topicId);
        if (onProductChange) onProductChange(topicId);
    };
    const [dimension, setDimension] = useState('location');
    const [npsData, setNpsData] = useState([]);
    const [timeSeriesData, setTimeSeriesData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'nps', dir: 'desc' });
    const [chartValues, setChartValues] = useState(new Set());
    const [timeRange, setTimeRange] = useState('3y');
    const [searchTerm, setSearchTerm] = useState('');
    const [drilldown, setDrilldown] = useState(null); // { value, posts }
    const [drilldownLoading, setDrilldownLoading] = useState(false);
    const [tablePage, setTablePage] = useState(0);

    // Fetch dimension data
    useEffect(() => {
        if (!selectedTopic) return;
        setIsLoading(true);
        setDrilldown(null);
        setTablePage(0);
        setSearchTerm('');

        apiPost('topic/dimension/all', { topic_id: selectedTopic, dimension })
            .then(r => r.json())
            .then(result => {
            const data = result.nps || [];
            setNpsData(data);
            setTimeSeriesData(result.timeseries || []);
            // Auto-select top 5 by count for chart
            const top5 = [...data]
                .sort((a, b) => b.count - a.count)
                .slice(0, 5)
                .map(r => r.dimension_value);
            setChartValues(new Set(top5));
        }).catch(err => {
            console.error('[DimensionNPSPage] fetch error:', err);
            setNpsData([]);
            setTimeSeriesData([]);
        }).finally(() => setIsLoading(false));
    }, [selectedTopic, dimension]);

    // Sorting
    const sortedData = useMemo(() => {
        const sorted = [...npsData];
        sorted.sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];
            if (typeof aVal === 'string') return sortConfig.dir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            return sortConfig.dir === 'asc' ? aVal - bVal : bVal - aVal;
        });
        return sorted;
    }, [npsData, sortConfig]);

    const filteredData = useMemo(() => {
        if (!searchTerm) return sortedData;
        const term = searchTerm.toLowerCase();
        return sortedData.filter(r => r.dimension_value.toLowerCase().includes(term));
    }, [sortedData, searchTerm]);

    const pagedData = useMemo(() => {
        const start = tablePage * ROWS_PER_PAGE;
        return filteredData.slice(start, start + ROWS_PER_PAGE);
    }, [filteredData, tablePage]);

    const totalPages = Math.ceil(filteredData.length / ROWS_PER_PAGE);

    const handleSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
        }));
        setTablePage(0);
    }, []);

    const toggleChart = useCallback((value) => {
        setChartValues(prev => {
            const next = new Set(prev);
            if (next.has(value)) next.delete(value);
            else next.add(value);
            return next;
        });
    }, []);

    const allSelected = filteredData.length > 0 && filteredData.every(r => chartValues.has(r.dimension_value));
    const someSelected = filteredData.some(r => chartValues.has(r.dimension_value));

    const toggleAll = useCallback(() => {
        setChartValues(prev => {
            const visibleValues = filteredData.map(r => r.dimension_value);
            const allIn = visibleValues.every(v => prev.has(v));
            const next = new Set(prev);
            if (allIn) {
                visibleValues.forEach(v => next.delete(v));
            } else {
                visibleValues.forEach(v => next.add(v));
            }
            return next;
        });
    }, [filteredData]);

    // Drill-down
    const handleDrilldown = useCallback(async (dimensionValue) => {
        if (drilldown?.value === dimensionValue) {
            setDrilldown(null);
            return;
        }
        setDrilldownLoading(true);
        try {
            const res = await apiPost('topic/dimension/posts', {
                topic_id: selectedTopic,
                dimension,
                dimension_value: dimensionValue,
                limit: 50,
            });
            const result = await res.json();
            setDrilldown({ value: dimensionValue, posts: result.data || [] });
        } catch (err) {
            console.error('[DimensionNPSPage] drilldown error:', err);
        } finally {
            setDrilldownLoading(false);
        }
    }, [selectedTopic, dimension, drilldown]);

    // Chart data
    const chartData = useMemo(() => {
        if (chartValues.size === 0 || timeSeriesData.length === 0) return null;

        const now = new Date();
        const rangeYears = { '1y': 1, '2y': 2, '3y': 3, '5y': 5, '10y': 10, 'all': 100 };
        const cutoff = new Date(now.getFullYear() - (rangeYears[timeRange] || 3), now.getMonth(), 1);
        const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}`;

        // Group by dimension value
        const byValue = {};
        for (const row of timeSeriesData) {
            if (!chartValues.has(row.dimension_value)) continue;
            if (row.month < cutoffStr) continue;
            if (!byValue[row.dimension_value]) byValue[row.dimension_value] = [];
            byValue[row.dimension_value].push(row);
        }

        // Collect all months
        const allMonths = new Set();
        for (const rows of Object.values(byValue)) {
            for (const r of rows) allMonths.add(r.month);
        }
        const labels = [...allMonths].sort();

        const datasets = [];
        let colorIdx = 0;
        for (const [value, rows] of Object.entries(byValue)) {
            const monthMap = {};
            for (const r of rows) monthMap[r.month] = r.nps;
            datasets.push({
                label: value,
                data: labels.map(m => monthMap[m] ?? null),
                borderColor: CHART_COLORS[colorIdx % CHART_COLORS.length],
                backgroundColor: 'transparent',
                borderWidth: 2,
                pointRadius: 1,
                tension: 0.3,
                spanGaps: true,
            });
            colorIdx++;
        }

        return { labels, datasets };
    }, [chartValues, timeSeriesData, timeRange]);

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } },
            tooltip: {
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: NPS ${ctx.parsed.y}`,
                },
            },
        },
        scales: {
            y: {
                min: -100, max: 100,
                title: { display: true, text: 'NPS' },
                grid: { color: '#f1f5f9' },
            },
            x: {
                grid: { display: false },
                ticks: { maxRotation: 45, font: { size: 10 } },
            },
        },
    }), []);

    if (isLoading) {
        return <div className="dimension-loading"><CircularProgress /></div>;
    }

    const dimensionLabel = DIMENSIONS.find(d => d.value === dimension)?.label || dimension;

    const columns = [
        { key: 'dimension_value', label: dimensionLabel, align: 'left' },
        { key: 'nps', label: 'NPS', align: 'right' },
        { key: 'count', label: 'Count', align: 'right' },
        { key: 'promoters', label: 'Promoters', align: 'right' },
        { key: 'passives', label: 'Passives', align: 'right' },
        { key: 'detractors', label: 'Detractors', align: 'right' },
        { key: 'avg_rating', label: 'Avg Rating', align: 'right' },
    ];

    return (
        <div className="dimension-nps-container">
            {/* Controls */}
            <div className="dimension-nps-controls">
                <ProductSelector
                    topicMap={topicMap}
                    selectedId={selectedTopic}
                    onSelect={selectTopic}
                />
                <FormControl size="small" variant="outlined">
                    <InputLabel id="dimension-select-label">Dimension</InputLabel>
                    <Select
                        labelId="dimension-select-label"
                        id="dimension-select"
                        value={dimension}
                        label="Dimension"
                        onChange={(e) => setDimension(e.target.value)}
                        sx={{
                            '& .MuiSelect-select': {
                                backgroundColor: 'white',
                                paddingRight: '32px !important'
                            }
                        }}
                    >
                        {DIMENSIONS.map(d => (
                            <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TimeRangeSelector
                    timeRange={timeRange}
                    onTimeRangeChange={setTimeRange}
                />
            </div>

            {/* Time series chart */}
            {chartData && chartData.datasets.length > 0 && (
                <div className="dimension-chart-section">
                    <div className="dimension-chart-container">
                        <Line data={chartData} options={chartOptions} />
                    </div>
                </div>
            )}

            {/* NPS Table */}
            {sortedData.length > 0 && (<>
                <div className="dimension-table-search">
                    <input
                        type="text"
                        placeholder={`Search ${DIMENSIONS.find(d => d.value === dimension)?.plural || dimensionLabel.toLowerCase() + 's'}...`}
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setTablePage(0); }}
                    />
                    {searchTerm && (
                        <span className="dimension-search-count">
                            {filteredData.length} of {sortedData.length}
                        </span>
                    )}
                </div>
                <div className="dimension-nps-table-wrapper">
                    <table className="dimension-nps-table">
                        <thead>
                            <tr>
                                <th style={{ width: 30 }}>
                                        <input
                                            type="checkbox"
                                            className="chart-toggle"
                                            checked={allSelected}
                                            ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                                            onChange={toggleAll}
                                            title={allSelected ? 'Deselect all' : 'Select all'}
                                        />
                                    </th>
                                {columns.map(col => (
                                    <th
                                        key={col.key}
                                        className={`${col.align === 'right' ? 'text-right' : ''} ${sortConfig.key === col.key ? `sorted-${sortConfig.dir}` : ''}`}
                                        onClick={() => handleSort(col.key)}
                                    >
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pagedData.map(row => (
                                <tr
                                    key={row.dimension_value}
                                    className={drilldown?.value === row.dimension_value ? 'selected' : ''}
                                    onClick={() => handleDrilldown(row.dimension_value)}
                                >
                                    <td>
                                        <input
                                            type="checkbox"
                                            className="chart-toggle"
                                            checked={chartValues.has(row.dimension_value)}
                                            onChange={(e) => { e.stopPropagation(); toggleChart(row.dimension_value); }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </td>
                                    <td>{row.dimension_value}</td>
                                    <td className={`text-right ${getNPSClass(row.nps)}`}>{row.nps}</td>
                                    <td className="text-right">{row.count}</td>
                                    <td className="text-right">{row.promoters}</td>
                                    <td className="text-right">{row.passives}</td>
                                    <td className="text-right">{row.detractors}</td>
                                    <td className="text-right">{row.avg_rating}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {totalPages > 1 && (
                        <div className="dimension-table-pagination">
                            <span>Showing {tablePage * ROWS_PER_PAGE + 1}-{Math.min((tablePage + 1) * ROWS_PER_PAGE, filteredData.length)} of {filteredData.length}</span>
                            <div>
                                <button disabled={tablePage === 0} onClick={() => setTablePage(p => p - 1)}>Prev</button>
                                {' '}
                                <button disabled={tablePage >= totalPages - 1} onClick={() => setTablePage(p => p + 1)}>Next</button>
                            </div>
                        </div>
                    )}
                </div>
            </>)}

            {/* Drill-down */}
            {drilldownLoading && <div className="dimension-loading"><CircularProgress size={24} /></div>}
            {drilldown && !drilldownLoading && (
                <div className="dimension-drilldown">
                    <div className="dimension-drilldown-header">
                        <h3>Reviews for: {drilldown.value} ({drilldown.posts.length} posts)</h3>
                        <button className="dimension-drilldown-close" onClick={() => setDrilldown(null)}>Close</button>
                    </div>
                    <FilteredPostsDisplay
                        posts={drilldown.posts.map(p => ({
                            id: p.post_id,
                            title: p.title,
                            text: p.text,
                            url: p.url,
                            time: p.time,
                            type: p.type,
                            score: p.score,
                            rating: p.rating,
                        }))}
                        isLoading={false}
                    />
                </div>
            )}

            {!isLoading && npsData.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    No dimension data available for this topic.
                </div>
            )}
        </div>
    );
}
